import CustomButton from '../../buttons/CustomButton';
import {
  ButtonTypes,
  OnlineShopWebViewPaymentResponseInterface,
  PaymentResult,
} from '../../../services/exports/Interfaces';
import useDeliveryThreshold from '../../../hooks/order/useDeliveryThreshold';
import useCheckoutThreshold from '../../../hooks/order/useCheckoutThreshold';
import { useTranslation } from 'react-i18next';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  OnlineShopWebViewMessageTypes,
  PaymentMethod,
  PaymentStatus,
  PaypalVariant,
  SCREENS,
  isSpreedlyOffsitePayment,
} from '../../../services/exports/Constants';
import useRouter from '../../../hooks/navigation/useRouter';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { PaymentIntent } from '@stripe/stripe-js';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import { Routes } from '../../../modules/mobile/exports/Enums';
import Alert from '../../../modules/mobile/components/modals/Alert';
import PaymentInitializer from '../../payment/PaymentInitializer';
import useOrderCheckout, {
  CheckoutFunction,
  CheckoutOptions,
  useGenerateOrderReturnUrl,
  useHandleOrderCheckoutResult,
} from '../../../hooks/payment/useOrderCheckout';
import AdyenPayButton from './AdyenPayButton';
import StripePayButton from './StripePayButton';
import SpreedlyPayButton from './SpreedlyPayButton';
import SpreedlyOffsitePayButton from './SpreedlyOffsitePayButton';
import PaypalDirectButtons from '../payment-methods/PaypalDirectButtons';
import { ChildPayButtonProps, PayButtonProps } from './types';
import { ConfirmPaymentFunction } from '../../../hooks/payment/useCheckout';
import TermsLink from '../../legal/TermsLink';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import { OrderData } from '../../../services/exports/Interfaces';
import { useStripeElementsSession } from '../StripeElementsMounted';

function DefaultButton(props: ChildPayButtonProps) {
  const { title, loading, disabled, silentlyDisabled, onClick, className } = props;

  const { isWithinThreshold } = useDeliveryThreshold();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();

  const confirmPayment = async (): Promise<PaymentResult> => ({
    status: PaymentStatus.Succeeded,
    error: null,
    result: null,
  });

  return (
    <>
      <span
        data-test="pay-button-state"
        data-ready={!disabled && !silentlyDisabled && !loading ? 'true' : 'false'}
        className="hidden"
      />
      <CustomButton
        // A3. Same reasoning as the checkout CTA: `silentlyDisabled` has no DOM signal, so a spec
        // needs both an addressable button and a readiness flag it can wait on.
        testId="pay-button"
        buttonType={!isWithinThreshold || !hasReachedCheckoutThreshold ? ButtonTypes.forth : ButtonTypes.primary}
        title={title}
        disabled={disabled}
        silentlyDisabled={silentlyDisabled}
        onClick={() => onClick(confirmPayment)}
        loading={loading}
        className={className}
        showArrow
      />
    </>
  );
}

function PayButtonContent({ showTerms, className }: PayButtonProps) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });
  const { t: tErrors } = useTranslation(undefined, { keyPrefix: 'Modules:Mobile:Shared:Errors' });

  const { navigate } = useRouter();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();
  const { isWithinMinThreshold, isWithinMaxThreshold, isWithinThreshold } = useDeliveryThreshold();
  const { selectedPaymentMethod, paypalVariant, getGatewayForMethod } = usePaymentFlow();
  const { data, loading: draftLoading, isDraftSettling } = useOrderFlow();
  const { formatCurrency } = useAppMethods();

  const {
    loading: paymentsLoading,
    isPaymentProcessing: loading,
    setIsPaymentProcessing: setLoading,
  } = usePaymentCoordinator();
  const { blockPending: paymentBlockPending } = useStripeElementsSession();
  const requiresPayment =
    hasReachedCheckoutThreshold &&
    isWithinMinThreshold &&
    isWithinMaxThreshold &&
    data.total > 0 &&
    selectedPaymentMethod !== PaymentMethod.Cash;

  const [isMobileAlertOpen, setIsMobileAlertOpen] = useState(false);
  const mobileAppGenericError = useRef(null);
  const toggleMobileAlert = () => setIsMobileAlertOpen((prev) => !prev);

  const { generateReturnUrl } = useGenerateOrderReturnUrl();
  const { onPaymentError, onPaymentSuccess } = useHandleOrderCheckoutResult({ setLoading });
  const { checkout } = useOrderCheckout({
    setLoading,
    onMobileAppCheckoutError: (response) => {
      mobileAppGenericError.current =
        response.response?.data?.description || response.response?.data?.title || tErrors('internal_error');
      toggleMobileAlert();
    },
  });

  useEffect(() => {
    document.addEventListener('message', resolveWebViewMessage);

    return () => document.removeEventListener('message', resolveWebViewMessage);
  }, []);

  const resolveWebViewMessage = (message: any): void => {
    if (message?.data?.type === OnlineShopWebViewMessageTypes.PaymentResult) {
      handlePaymentResultFromWebView(message.data.payload);
    }
  };

  const handlePaymentResultFromWebView = (payload: OnlineShopWebViewPaymentResponseInterface): boolean => {
    const { payment_result, order } = payload;

    if (payment_result.status === PaymentStatus.Succeeded) {
      return onPaymentSuccess(order);
    }

    return onPaymentError();
  };

  const title = useMemo(() => {
    if (!hasReachedCheckoutThreshold || !isWithinMinThreshold) {
      return t('add_items');
    }

    if (!isWithinMaxThreshold) {
      return t('remove_items');
    }

    if (data.total === 0) {
      return t('place_order');
    }

    return t('pay_now', { amount: formatCurrency(data?.total ?? 0) });
  }, [i18n.language, data.total, isWithinMinThreshold, isWithinMaxThreshold, hasReachedCheckoutThreshold]);

  async function onClick(
    confirmPayment: ConfirmPaymentFunction,
    paymentIntent?: PaymentIntent,
    customCheckout?: CheckoutFunction,
    options?: CheckoutOptions,
    extraData?: Partial<OrderData>,
  ): Promise<boolean> {
    if (!isWithinThreshold || !hasReachedCheckoutThreshold) {
      navigate({
        web: {
          to: SCREENS.HOME_SCREEN,
        },
      });

      return true;
    }

    const checkoutFunction = customCheckout ?? checkout;

    return await checkoutFunction(confirmPayment, paymentIntent, extraData, undefined, options);
  }

  // PayPal-direct is captured by its own PayPal SDK buttons, rendered here in the same
  // bottom slot as the other methods' pay button (consistent placement when switching
  // payment methods).
  if (selectedPaymentMethod === PaymentMethod.Paypal && paypalVariant === PaypalVariant.Direct) {
    return (
      <>
        {/* Same readiness marker as the other branches, so the e2e helpers can wait on this slot too.
            The SDK draws the button itself; the wrapper is the app-owned handle. */}
        <span
          data-test="pay-button-state"
          data-ready={!loading && !paymentsLoading && !draftLoading ? 'true' : 'false'}
          className="hidden"
        />
        <div data-test="paypal-direct-buttons">
          <PaypalDirectButtons />
        </div>
        {showTerms && <TermsLink />}
      </>
    );
  }

  const childProps = {
    title,
    loading,
    disabled: data.total > 0 && !selectedPaymentMethod,
    // Behind the Stripe Elements checkout's placeholder, whatever is selected is not on screen yet. A
    // free order with nothing selected relies on the draft being current to be placed as cash; a click
    // during the redraft debounce would otherwise be told to choose a method it does not need.
    silentlyDisabled:
      paymentsLoading ||
      draftLoading ||
      paymentBlockPending ||
      (isDraftSettling && data.total === 0 && !selectedPaymentMethod),
    setLoading,
    onClick,
    onPaymentSuccess,
    onPaymentError,
    generateReturnUrl,
    checkout,
    className,
  };

  const routedGateway = getGatewayForMethod(selectedPaymentMethod);
  const PaymentButtonComponent = requiresPayment
    ? ({
        adyen: AdyenPayButton,
        stripe: StripePayButton,
        // Offsite methods share the gateway but not the flow: no card to tokenize, a bank or wallet to visit.
        spreedly: isSpreedlyOffsitePayment(selectedPaymentMethod) ? SpreedlyOffsitePayButton : SpreedlyPayButton,
      }[routedGateway] ?? DefaultButton)
    : DefaultButton;

  return (
    <>
      <Alert
        title={mobileAppGenericError.current}
        isOpen={isMobileAlertOpen}
        onDidDismiss={toggleMobileAlert}
        buttons={[
          {
            text: t('go_to_menu'),
            handler: () =>
              navigate({
                mobile: {
                  path: Routes.Main,
                  payload: {
                    tab: 'Menu',
                  },
                },
              }),
          },
        ]}
      />
      <PaymentButtonComponent {...childProps} />
      {showTerms && <TermsLink />}
    </>
  );
}

export default function PayButton(props: PayButtonProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const { setIsGiftCardCheckout } = usePaymentCoordinator();

  useEffect(() => {
    setIsGiftCardCheckout(false);
  }, []);

  return (
    <PaymentInitializer fallback={<CustomButton title={t('secure_checkout')} loading className="mt-sm" />}>
      <PayButtonContent {...props} />
    </PaymentInitializer>
  );
}
